import { useGetFlags } from '@/services/flags'
import { colors } from '@/theme'
import type { MedicationRequest, ParsedQuestionnaire } from '@/services/med-req-detail'
import { Image } from '@mui/icons-material'
import { Box, Link, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import CollapsibleSection from './collapsible-section'

function FlagCell({ flags, batch }: { flags: string[]; batch?: boolean }) {
  const { data: flagsData } = useGetFlags()

  if (!flagsData || !flags?.length || batch) {
    return null
  }

  const flagTitles = flags
    .map(flag => flagsData[flag]?.title)
    .filter(Boolean)
    .join(', ')

  if (!flagTitles) return null

  return (
    <Typography sx={{ color: colors.text.secondary, fontSize: '14px', lineHeight: 1.7, wordBreak: 'break-word' }}>
      {flagTitles}
    </Typography>
  )
}

function ImageLink({ href, label }: { href: string; label: string }) {
  return (
    <Link href={href} target='_blank' rel='noopener noreferrer' underline='hover' sx={{ cursor: 'pointer' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Image sx={{ fontSize: '16px', color: colors.text.secondary }} />
        <Typography sx={{ fontSize: '14px', color: colors.brand.primary }}>{label}</Typography>
      </Box>
    </Link>
  )
}

function AnswerCell({ item }: { item: ParsedQuestionnaire }) {
  if (item.imageUploaded) {
    const links = item.links || (item.link ? [item.link] : [])

    if (links.length === 0) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Image sx={{ fontSize: '16px', color: colors.text.secondary }} />
          <Typography sx={{ fontSize: '14px', color: colors.text.secondary }}>Image Uploaded</Typography>
        </Box>
      )
    }

    if (links.length === 1) {
      return <ImageLink href={links[0]} label='Image Uploaded' />
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {links.map((link, index) => (
          <ImageLink key={index} href={link} label={`Image ${index + 1}`} />
        ))}
      </Box>
    )
  }

  const answer = item.answer
  const displayAnswer = Array.isArray(answer) ? answer.join(', ') : answer

  return (
    <Typography sx={{ color: colors.text.secondary, fontSize: '14px', lineHeight: 1.7, wordBreak: 'break-word' }}>
      {displayAnswer || '-'}
    </Typography>
  )
}

const cellSx = {
  py: 2,
  px: 3,
  borderBottom: `1px solid ${colors.border.default}`
} as const

const headerCellSx = {
  ...cellSx,
  color: colors.text.dark,
  fontSize: '14px',
  fontWeight: 600
} as const

interface ConsultationProps {
  medRequest: MedicationRequest
}

export default function Consultation({ medRequest }: ConsultationProps) {
  const consultationData = medRequest?.consultationData || []

  if (!consultationData.length) return null

  return (
    <CollapsibleSection id='consultation' title='Consultation' defaultOpen={false}>
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell sx={headerCellSx}>Question</TableCell>
            <TableCell sx={headerCellSx}>Answer</TableCell>
            <TableCell sx={headerCellSx}>Flag</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {consultationData.map((item, index) => (
            <TableRow
              key={index}
              sx={{
                bgcolor: item.hasFlags ? colors.status.errorLight : 'transparent',
                '&:last-child td': { borderBottom: 'none' }
              }}
            >
              <TableCell sx={{ ...cellSx, maxWidth: 320 }}>
                <Typography sx={{ color: colors.text.dark, fontSize: '14px', fontWeight: 500, lineHeight: 1.7 }}>
                  {item.title}
                </Typography>
              </TableCell>
              <TableCell sx={{ ...cellSx, maxWidth: 320 }}>
                <AnswerCell item={item} />
              </TableCell>
              <TableCell sx={{ ...cellSx, maxWidth: 320 }}>
                <FlagCell flags={item.flags} batch={item.batch} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CollapsibleSection>
  )
}
